// Helpers for Android Material theme colors.
//
// Android encodes colors as #AARRGGBB (alpha FIRST). CSS hex uses #RRGGBBAA
// (alpha LAST), so converting to a CSS-renderable value means moving the alpha
// pair from the front to the back. We also accept plain #RRGGBB (assumed opaque).

const ARGB_RE = /^#([0-9a-fA-F]{8})$/;
const RGB_RE = /^#([0-9a-fA-F]{6})$/;

export function isValidAndroidColor(value: string): boolean {
    const v = value.trim();
    return ARGB_RE.test(v) || RGB_RE.test(v);
}

/** Convert an Android #AARRGGBB (or #RRGGBB) color to a CSS color string, or null if invalid. */
export function androidColorToCss(value: string): string | null {
    const v = value.trim();
    if (RGB_RE.test(v)) return v; // already CSS-valid
    const m = ARGB_RE.exec(v);
    if (!m) return null;
    const hex = m[1];
    const alpha = hex.slice(0, 2);
    const rgb = hex.slice(2); // RRGGBB
    return `#${rgb}${alpha}`; // CSS #RRGGBBAA
}

/** Split an Android color into its alpha (0-255) and #RRGGBB parts. Falls back to opaque black when invalid. */
export function splitAndroidColor(value: string): { alpha: number; rgb: string } {
    const v = value.trim();
    if (RGB_RE.test(v)) return { alpha: 255, rgb: v.toUpperCase() };
    const m = ARGB_RE.exec(v);
    if (!m) return { alpha: 255, rgb: "#000000" };
    const hex = m[1];
    return { alpha: parseInt(hex.slice(0, 2), 16), rgb: `#${hex.slice(2).toUpperCase()}` };
}

/** Combine an alpha (0-255) and a #RRGGBB value into an Android #AARRGGBB string. */
export function joinAndroidColor(alpha: number, rgb: string): string {
    const a = Math.max(0, Math.min(255, Math.round(alpha)))
        .toString(16)
        .padStart(2, "0");
    const rr = rgb.replace("#", "");
    return `#${a}${rr}`.toUpperCase();
}
